/**
 * ``retail.*`` event schemas — the live customer surface (``/retail``).
 *
 * The Retail Investors V3 IA (Market Pulse / Profile / Demand Radar /
 * Product Fit / Assets, plus the vanEck briefing / editorial / strategist
 * reads) replaced the legacy ``marketing.dashboard.* / assets_list.*``
 * surface, which now only REDIRECTS to ``/retail`` (next.config). Those
 * legacy ``marketing.*`` events therefore no longer fire on the product
 * customers actually use; this file is the instrumentation for the live
 * surface.
 *
 * Module: every event carries ``module: "marketing"`` — /retail is the
 * Marketing module's surface (see frontend CLAUDE.md § "Navigation &
 * access scope"). The ``retail.*`` name prefix is the AREA, deliberately
 * distinct from the retired ``marketing.*`` legacy-IA event names.
 *
 * Privacy: pageview + filter/interaction SHAPE only. The Ask-Demand
 * question text (user content) is never captured — only that a question
 * was asked. Person identity rides on ``$identify``, never on these
 * events (and the ``before_send`` redactor strips forbidden keys anyway).
 *
 * Pattern: each schema extends ``BaseEventSchema``; the trailing word of
 * segment 3 is a whitelisted past-tense verb (``./governance/verbs``).
 */

import { z } from "zod";

import { BaseEventSchema } from "./base";
import {
  RetailAssetClassProperty,
  RetailScopeProperty,
  RetailWindowProperty,
} from "./properties";

// ── Pageviews (one per /retail route, fired once on mount) ─

export const RetailPulseViewedSchema = BaseEventSchema.extend({
  event_name: z.literal("retail.pulse.viewed"),
  screen: z.string(),
});

export const RetailProfileViewedSchema = BaseEventSchema.extend({
  event_name: z.literal("retail.profile.viewed"),
  screen: z.string(),
});

export const RetailRadarViewedSchema = BaseEventSchema.extend({
  event_name: z.literal("retail.radar.viewed"),
  screen: z.string(),
});

export const RetailFitViewedSchema = BaseEventSchema.extend({
  event_name: z.literal("retail.fit.viewed"),
  screen: z.string(),
});

export const RetailAssetsViewedSchema = BaseEventSchema.extend({
  event_name: z.literal("retail.assets.viewed"),
  screen: z.string(),
});

export const RetailBriefingViewedSchema = BaseEventSchema.extend({
  event_name: z.literal("retail.briefing.viewed"),
  screen: z.string(),
});

export const RetailEditorialViewedSchema = BaseEventSchema.extend({
  event_name: z.literal("retail.editorial.viewed"),
  screen: z.string(),
});

export const RetailStrategistViewedSchema = BaseEventSchema.extend({
  event_name: z.literal("retail.strategist.viewed"),
  screen: z.string(),
});

// ── Shared market-scope filters (scope-context.tsx) ───────

export const RetailScopeChangedSchema = BaseEventSchema.extend({
  event_name: z.literal("retail.scope.changed"),
  // ``market`` not ``country`` — the scope can be the pooled LATAM region.
  market: RetailScopeProperty,
});

export const RetailWindowChangedSchema = BaseEventSchema.extend({
  event_name: z.literal("retail.window.changed"),
  window_days: RetailWindowProperty,
});

export const RetailAssetClassFilteredSchema = BaseEventSchema.extend({
  event_name: z.literal("retail.asset_class.filtered"),
  asset_class: RetailAssetClassProperty,
});

// ── Map + ask interactions ────────────────────────────────

export const RetailMapPanelSwitchedSchema = BaseEventSchema.extend({
  event_name: z.literal("retail.map.panel_switched"),
  // The map's per-country panel state (UI control state, not PII).
  panel: z.string(),
});

export const RetailAskSubmittedSchema = BaseEventSchema.extend({
  event_name: z.literal("retail.ask.submitted"),
  // No question text — user content never rides on a telemetry event.
  source: z.enum(["typed", "suggested"]).optional(),
});

export const retailEvents = [
  RetailPulseViewedSchema,
  RetailProfileViewedSchema,
  RetailRadarViewedSchema,
  RetailFitViewedSchema,
  RetailAssetsViewedSchema,
  RetailBriefingViewedSchema,
  RetailEditorialViewedSchema,
  RetailStrategistViewedSchema,
  RetailScopeChangedSchema,
  RetailWindowChangedSchema,
  RetailAssetClassFilteredSchema,
  RetailMapPanelSwitchedSchema,
  RetailAskSubmittedSchema,
] as const;
